import { useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { callApi } from '@/hooks/useFetch'

export const MAX_JOB_POLL_DURATION_MS = 10 * 60 * 1000

export interface GenericJobStatusResponse {
  job_id: string
  status: string
  progress: number
  current_item: number
  total_items: number
  message: string
  result?: Record<string, unknown>
  errors: string[]
}

const timeoutStatus = (jobId: string, maxDurationMs: number): GenericJobStatusResponse => {
  const message = `Timed out after ${Math.round(maxDurationMs / 60000)} min (the job may still be running in the background)`
  return {
    job_id: jobId,
    status: 'failed',
    progress: 0,
    current_item: 0,
    total_items: 0,
    message,
    errors: [message],
  }
}

export function useGenericJobStatus(
  jobId: string | null,
  enabled: boolean = true,
  maxDurationMs: number = MAX_JOB_POLL_DURATION_MS,
) {
  const pollStartRef = useRef<{ jobId: string | null; startedAt: number }>({
    jobId: null,
    startedAt: 0,
  })
  if (pollStartRef.current.jobId !== jobId) {
    pollStartRef.current = { jobId, startedAt: Date.now() }
  }

  return useQuery({
    queryKey: ['jobStatus', jobId],
    queryFn: async (): Promise<GenericJobStatusResponse> => {
      if (!jobId) {
        throw new Error('No job ID provided')
      }

      if (Date.now() - pollStartRef.current.startedAt > maxDurationMs) {
        return timeoutStatus(jobId, maxDurationMs)
      }

      return callApi<GenericJobStatusResponse>(`/jobs/${jobId}`)
    },
    enabled: enabled && !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data || data.status === 'completed' || data.status === 'failed') {
        return false
      }
      return 2000
    },
    refetchIntervalInBackground: true,
  })
}
